// src/components/DdosTesting/ProtectionAnalysis.jsx
import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';

const ProtectionAnalysis = ({ protections = [], recommendations = [] }) => {
    const { t } = useLanguage();

    const hasProtectionData = protections.length > 0;
    const hasRecommendationData = recommendations.length > 0;

    if (!hasProtectionData && !hasRecommendationData) return null;

    const getProtectionStyle = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
            case 'detected':
                return { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/15', icon: 'fa-check-circle' };
            case 'partial':
            case 'suspected':
                return { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/15', icon: 'fa-exclamation-triangle' };
            case 'inactive':
            case 'bypassed':
                return { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/15', icon: 'fa-times-circle' };
            default:
                return { color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/15', icon: 'fa-question-circle' };
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="relative overflow-hidden bg-[#0e0e0e] border border-emerald-500/10 rounded-2xl sm:rounded-3xl"
            style={{
                backgroundImage: 'radial-gradient(ellipse 400px 200px at 10% 0%, rgba(16,185,129,0.03) 0%, transparent 70%)',
            }}
        >
            {/* Top accent */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-400/30 to-transparent" />

            <div className="p-4 sm:p-8">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/15 flex items-center justify-center">
                        <i className="fas fa-shield-halved text-emerald-400"></i>
                    </div>
                    <h3 className="text-lg font-bold text-white">{t('ddosProtectionAnalysis') || 'Protection Analysis'}</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Detected Protections */}
                    <div className="bg-[#0a0a0a] border border-white/[0.04] rounded-2xl p-5">
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">
                            {t('ddosDetectedProtection') || 'Detected'}
                        </h4>
                        {!hasProtectionData ? (
                            <p className="text-gray-600 text-sm">{t('ddosNoProtectionData') || 'No protection detected'}</p>
                        ) : (
                            <div className="space-y-3">
                                {protections.map((p, i) => {
                                    const style = getProtectionStyle(p.status);
                                    return (
                                        <div key={i} className={`flex items-start gap-3 p-3 rounded-xl ${style.bg} border ${style.border}`}>
                                            <div className={`w-8 h-8 rounded-lg ${style.bg} flex items-center justify-center shrink-0`}>
                                                <i className={`fas ${style.icon} ${style.color} text-sm`}></i>
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-semibold text-white text-sm">{p.name || (t('ddosUnknownProtection') || 'Unknown')}</p>
                                                <p className="text-xs text-gray-500 mt-0.5">{p.description || (t('ddosNoDescription') || 'No details')}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Recommendations */}
                    <div className="bg-[#0a0a0a] border border-white/[0.04] rounded-2xl p-5">
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">
                            {t('ddosRecommendations') || 'Recommendations'}
                        </h4>
                        {!hasRecommendationData ? (
                            <p className="text-gray-600 text-sm">{t('ddosNoRecommendations') || 'No recommendations'}</p>
                        ) : (
                            <div className="space-y-3">
                                {recommendations.map((rec, i) => (
                                    <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-blue-500/[0.04] border border-blue-500/10">
                                        <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                                            <i className="fas fa-lightbulb text-blue-400 text-sm"></i>
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-semibold text-white text-sm">{rec.title || (t('ddosRecommendation') || 'Recommendation')}</p>
                                            <p className="text-xs text-gray-500 mt-0.5">{rec.description || (t('ddosNoDetailsProvided') || 'No details')}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default ProtectionAnalysis;